import type { CSSProperties } from "react";
import type { HeroSectionData } from "../types";

/**
 * Shared helpers that map the Hero editor's generic controls
 * (height, spacing, responsive, background, text colours/sizes,
 * container width) onto runtime styles + classes. Every helper is
 * fallback-safe: when a control is unset it returns `undefined`/empty
 * so each hero variant keeps its own opinionated default.
 */

const HEIGHT_MINH: Record<string, string> = {
  small: "250px",
  medium: "400px",
  large: "550px",
  fullscreen: "100vh",
};

/** Section min-height from the `height` control (else `fallback`). */
export function heroMinHeight(
  section: HeroSectionData,
  fallback?: string,
): string | undefined {
  const h = (section as any).height as string | undefined;
  return (h && HEIGHT_MINH[h]) || fallback;
}

const SPACE: Record<string, string> = {
  none: "0",
  sm: "1rem",
  md: "2rem",
  lg: "4rem",
  xl: "6rem",
};
function sv(v?: string): string | undefined {
  if (!v) return undefined;
  return SPACE[v] ?? v;
}

/** Padding/margin from the `spacing` control. */
export function heroSpacingStyle(section: HeroSectionData): CSSProperties {
  const sp = (section as any).spacing || {};
  return {
    paddingTop: sv(sp.paddingTop),
    paddingBottom: sv(sp.paddingBottom),
    paddingLeft: sv(sp.paddingLeft),
    paddingRight: sv(sp.paddingRight),
    marginTop: sv(sp.marginTop),
    marginBottom: sv(sp.marginBottom),
  };
}

/** Hide-on-breakpoint classes from `responsiveSettings`. */
export function heroResponsiveClasses(section: HeroSectionData): string {
  const r = (section as any).responsiveSettings || {};
  const c: string[] = [];
  if (r.hideOnMobile) c.push("hidden md:block");
  if (r.hideOnTablet) c.push("md:hidden lg:block");
  if (r.hideOnDesktop) c.push("lg:hidden");
  return c.join(" ");
}

/** Max-width style for the inner content container from `containerSettings`. */
export function heroContainerStyle(section: HeroSectionData): CSSProperties {
  const cs = (section as any).containerSettings || {};
  if (cs.width === "custom" && cs.maxWidth) return { maxWidth: cs.maxWidth };
  if (cs.width === "fullWidth") return { maxWidth: "none" };
  return {};
}

function gradientCss(g: any): string | undefined {
  if (!g || !Array.isArray(g.stops) || g.stops.length === 0) return undefined;
  const stops = g.stops
    .map((s: any) => `${s.color} ${s.position}%`)
    .join(", ");
  return g.type === "radial"
    ? `radial-gradient(circle, ${stops})`
    : `linear-gradient(${g.angle ?? 135}deg, ${stops})`;
}

export interface ResolvedHeroBackground {
  imageUrl?: string;
  videoUrl?: string;
  cssBackground?: string; // solid colour or gradient
}

/**
 * Resolve the hero background from the structured `background` control
 * (image/video/solid/gradient), falling back to the legacy
 * `backgroundImage` / `backgroundVideo` fields.
 */
export function resolveHeroBackground(
  section: HeroSectionData,
): ResolvedHeroBackground {
  const bg = (section as any).background;
  const legacyImage = (section as any).backgroundImage as string | undefined;
  const legacyVideo = (section as any).backgroundVideo as string | undefined;

  if (bg && bg.type && bg.type !== "none") {
    if (bg.type === "image") return { imageUrl: bg.image || legacyImage };
    if (bg.type === "video") return { videoUrl: bg.video || legacyVideo };
    if (bg.type === "solid") return { cssBackground: bg.solid };
    if (bg.type === "gradient")
      return { cssBackground: gradientCss(bg.gradient) };
  }
  if (legacyVideo) return { videoUrl: legacyVideo };
  if (legacyImage) return { imageUrl: legacyImage };
  return {};
}

/** Headline colour/size overrides from `textColors`/`headlineSize`. */
export function heroHeadlineStyle(section: HeroSectionData): CSSProperties {
  const tc = (section as any).textColors || {};
  const size = (section as any).headlineSize as number | undefined;
  return {
    color: tc.headline || undefined,
    fontSize: size ? `${size}px` : undefined,
  };
}

/** Subheadline colour/size overrides. */
export function heroSubheadStyle(section: HeroSectionData): CSSProperties {
  const tc = (section as any).textColors || {};
  const size = (section as any).subheadlineSize as number | undefined;
  return {
    color: tc.subheadline || undefined,
    fontSize: size ? `${size}px` : undefined,
  };
}

/** Button text colour override from `textColors.buttons`. */
export function heroButtonColor(section: HeroSectionData): string | undefined {
  return (section as any).textColors?.buttons || undefined;
}

/**
 * Optional hero logo, rendered INLINE in the content flow (never absolute —
 * that overlapped the title). `logoPosition` maps to:
 *   vertical   top/middle prefixes → above the content, bottom prefix → below
 *   horizontal left/center/right suffixes → flex justify of the logo row
 * So all 9 positions change the layout (and the editor preview) live.
 */
export function heroLogo(section: HeroSectionData): {
  url?: string;
  below: boolean;
  justifyClass: string;
} {
  const pos = String((section as any).logoPosition || 'top-center');
  const justifyClass = pos.endsWith('left')
    ? 'justify-start'
    : pos.endsWith('right')
      ? 'justify-end'
      : 'justify-center';
  return {
    url: (section as any).logo || undefined,
    below: pos.startsWith('bottom'),
    justifyClass,
  };
}
